import {
  type DesignBody,
  type FaceKey,
  isPointInShape,
  type ResolvedEditAreaDefinition,
} from '@repo/api-contracts';
import {
  hitTestLayers,
  type PhotoDimensions,
  surfacePhotoToMm,
  wrappedPlacementSpans,
} from '@repo/design-renderer';

export interface PreviewHit {
  readonly componentId: string;
  readonly nodeId: string;
}

export interface PreviewHitInput {
  readonly definition: ResolvedEditAreaDefinition;
  readonly faceKey: FaceKey;
  readonly body: DesignBody;
  readonly photo: PhotoDimensions;
  /** 사진 px. */
  readonly point: { readonly x: number; readonly y: number };
}

interface ClientRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** 캔버스 표시 사각형(CSS px) 기준 클릭 위치를 사진 px 로 바꾼다. 줌·DPR·스크롤은 rect 가 흡수한다. */
export const clientToPhotoPx = (
  client: { x: number; y: number },
  rect: ClientRect,
  photo: PhotoDimensions,
) => ({
  x: ((client.x - rect.left) * photo.widthPx) / rect.width,
  y: ((client.y - rect.top) * photo.heightPx) / rect.height,
});

/**
 * 역워프 히트테스트 체인(T1, 스펙 §6-4): 사진 px → 표면 mm → 배치 위부터(D13) 컴포넌트 로컬 mm(감김 조각 반영) → 노드 판정.
 */
export const hitTestPreview = ({
  definition,
  faceKey,
  body,
  photo,
  point,
}: PreviewHitInput): PreviewHit | null => {
  const face = definition.faces[faceKey];
  if (face === undefined) return null;
  for (let index = face.placements.length - 1; index >= 0; index -= 1) {
    const placement = face.placements[index];
    if (placement === undefined) continue;
    const surface = face.surfaces.find((candidate) => candidate.id === placement.surfaceId);
    const component = definition.components.find(
      (candidate) => candidate.id === placement.componentId,
    );
    const layers =
      body.components.find((candidate) => candidate.componentId === placement.componentId)
        ?.layers ?? [];
    if (surface === undefined || component === undefined || layers.length === 0) continue;
    const surfaceMm = surfacePhotoToMm(surface, photo, point);
    if (surfaceMm === null) continue;
    const yMm = surfaceMm.yMm - placement.position.yMm;
    if (yMm < 0 || yMm > component.heightMm) continue;
    const origins =
      surface.type === 'cylinder'
        ? wrappedPlacementSpans(placement.position.xMm, component.widthMm, surface.realSize.widthMm)
            .filter((span) => surfaceMm.xMm >= span.fromMm && surfaceMm.xMm <= span.toMm)
            .map((span) => span.originMm)
        : [placement.position.xMm];
    for (const originMm of origins) {
      const xMm = surfaceMm.xMm - originMm;
      if (xMm < 0 || xMm > component.widthMm) continue;
      if (!isPointInShape(component, { xMm, yMm })) continue;
      const hit = hitTestLayers(layers, { xMm, yMm });
      if (hit !== null)
        return { componentId: component.id, nodeId: hit.groupPath[0] ?? hit.nodeId };
    }
  }
  return null;
};
